import { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, ChevronRight, CheckCircle2, HelpCircle, Loader2,
} from 'lucide-react';
import { ReceiptViewerPanel } from './ReceiptViewerPanel';

function fmtCurrency(val) {
  const num = Number(val);
  if (!Number.isFinite(num)) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
}

/**
 * Post-AI-extraction disambiguation step.
 *
 * Receipts with multi-quantity lines are ambiguous: the printed number can
 * be either a per-unit price OR the combined total for all units. The AI
 * preserves the raw printed number as `detected_price` and we let the user
 * pick:
 *   • Keep   — use detected_price for each item (line total = qty × detected)
 *   • Split  — split detected_price across qty items (line total = detected)
 *
 * The original receipt is shown next to the question so the user can
 * cross-reference printed totals before deciding.
 */
export function ClarifyQuantityView({ receipt, items, onAnswer, onBack, onContinue, saving }) {
  // Multi-qty rows are the only ones this step cares about.
  const ambiguous = useMemo(
    () => items.filter(it => Number(it.quantity) > 1),
    [items],
  );
  const remaining = ambiguous.filter(it => !it.qty_clarified).length;
  const allAnswered = remaining === 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="clarify-qty-view">
      {/* LEFT: original receipt for cross-reference */}
      <div>
        <p className="text-[10px] uppercase tracking-wider text-[#5b6773] font-semibold mb-2">
          Original receipt — verify totals here
        </p>
        <ReceiptViewerPanel mode="embedded" receipt={receipt} title="Receipt" />
      </div>

      {/* RIGHT: clarification cards */}
      <div>
        <div className="flex items-start gap-3 mb-5">
          <div className="h-10 w-10 rounded-full bg-[#2d948c]/10 flex items-center justify-center flex-shrink-0">
            <HelpCircle className="h-5 w-5 text-[#2d948c]" />
          </div>
          <div className="min-w-0">
            <h2 className="font-heading text-xl font-semibold text-[#1f2933]">
              Confirm multi-quantity prices
            </h2>
            <p className="text-sm text-[#5b6773] mt-1">
              For each line below, decide whether the receipt's printed price
              applies to <span className="font-medium text-[#1f2933]">each unit</span> (keep)
              or is the <span className="font-medium text-[#1f2933]">total</span> for all
              units combined (split).
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {ambiguous.map(it => {
            const qty = Number(it.quantity) || 1;
            // detected_price is the raw number printed on the receipt for
            // that line. Older receipts may not have it — fall back to
            // unit_price so we still render something sensible.
            const detected = Number(
              it.detected_price != null ? it.detected_price : it.unit_price,
            ) || 0;
            const keepLineTotal = +(qty * detected).toFixed(2);
            const splitEach = qty > 0 ? +(detected / qty).toFixed(2) : detected;
            const answered = !!it.qty_clarified;
            // After answering, derive which mode is active by comparing the
            // current line_total to the two candidate totals.
            const currentMode = answered
              ? Math.abs(Number(it.line_total) - keepLineTotal) < 0.01
                ? 'keep'
                : 'split'
              : null;

            return (
              <Card
                key={it.id}
                className={`overflow-hidden transition-colors ${
                  answered ? 'border-emerald-300/60 bg-emerald-50/30' : 'border-[#2d948c]/20'
                }`}
                data-testid={`clarify-row-${it.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-[#1f2933] truncate">
                        {it.name || '(unnamed item)'}
                      </p>
                      <p className="text-xs text-[#5b6773] mt-0.5">
                        Quantity: <span className="font-semibold text-[#1f2933]">{qty}</span>
                        <span className="mx-1.5 text-[#5b6773]/50">·</span>
                        Receipt price: <span className="font-semibold text-[#1f2933]">{fmtCurrency(detected)}</span>
                      </p>
                    </div>
                    {answered && (
                      <span className="flex items-center gap-1 text-[11px] text-emerald-700 font-medium flex-shrink-0">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Confirmed
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {/* Keep — apply the printed number to each unit */}
                    <button
                      type="button"
                      onClick={() => onAnswer(it.id, 'keep')}
                      data-testid={`clarify-keep-${it.id}`}
                      className={`text-left rounded-lg border px-3 py-2.5 transition-all ${
                        currentMode === 'keep'
                          ? 'border-[#2d948c] bg-[#2d948c]/10 ring-1 ring-[#2d948c]'
                          : 'border-[#e6e2d7] hover:border-[#2d948c]/60 hover:bg-[#2d948c]/5'
                      }`}
                    >
                      <p className="text-[11px] uppercase tracking-wider text-[#5b6773] font-semibold">
                        Keep {fmtCurrency(detected)} for each
                      </p>
                      <p className="text-sm text-[#1f2933] mt-0.5 tabular-nums">
                        {qty} × {fmtCurrency(detected)} = <span className="font-semibold">{fmtCurrency(keepLineTotal)}</span>
                      </p>
                      <p className="text-[11px] text-[#5b6773] mt-0.5">
                        Use when the receipt shows the per-unit price.
                      </p>
                    </button>

                    {/* Split — divide the printed number across all units */}
                    <button
                      type="button"
                      onClick={() => onAnswer(it.id, 'split')}
                      data-testid={`clarify-split-${it.id}`}
                      className={`text-left rounded-lg border px-3 py-2.5 transition-all ${
                        currentMode === 'split'
                          ? 'border-[#2d948c] bg-[#2d948c]/10 ring-1 ring-[#2d948c]'
                          : 'border-[#e6e2d7] hover:border-[#2d948c]/60 hover:bg-[#2d948c]/5'
                      }`}
                    >
                      <p className="text-[11px] uppercase tracking-wider text-[#5b6773] font-semibold">
                        Split {fmtCurrency(detected)} across {qty}
                      </p>
                      <p className="text-sm text-[#1f2933] mt-0.5 tabular-nums">
                        {fmtCurrency(splitEach)} each · line total <span className="font-semibold">{fmtCurrency(detected)}</span>
                      </p>
                      <p className="text-[11px] text-[#5b6773] mt-0.5">
                        Use when the receipt shows the combined total.
                      </p>
                    </button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <Button variant="ghost" onClick={onBack} className="gap-1.5" data-testid="clarify-back-btn">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          <div className="flex items-center gap-3">
            {!allAnswered && (
              <p className="text-xs text-[#5b6773]" data-testid="clarify-remaining">
                {remaining} item{remaining === 1 ? '' : 's'} left to confirm
              </p>
            )}
            <Button
              onClick={onContinue}
              disabled={!allAnswered || saving}
              className="gap-1.5"
              data-testid="clarify-continue-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Continue to review <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
